/**
 * Role-Based Access Control (RBAC)
 * ─────────────────────────────────
 * Defines roles, permissions, and the auto-assignment routing matrix for
 * tasks and approvals across ClearEqual.
 *
 * Roles
 * ─────
 *   admin    , Org-wide HR admin. Can create cases, propose changes, run reports.
 *   approver , Senior HR / works council rep. Required to ratify sensitive changes
 *               (status transitions on Article 9 cases, assignee changes after
 *               remediation begins, closure of joint assessments). Cannot self-approve
 *              , an approver who proposes a change still needs a second approver.
 *   manager  , Department head. Can be assignee for tasks within their dept;
 *               read access to their dept's data only.
 *   auditor  , External auditor / regulator. Read-only across compliance reports
 *               and audit trail. Never an assignee, never an approver.
 *   employee , End user. Self-service portal only. Cannot see or assign tasks.
 *
 * Why an approver role exists
 * ────────────────────────────
 * EU Pay Transparency Directive Art. 10(3) requires joint assessments to be
 * carried out "in cooperation with workers' representatives". Closing a joint
 * assessment, changing remediation actions, or reassigning a case mid-flight
 * are all decisions that should not rest with a single admin's click. The
 * approver role enforces a second pair of eyes on these state transitions.
 *
 * Two-person rule
 * ───────────────
 * For sensitive operations marked `requiresApproval: true` below, the system
 * creates a PENDING change. The change is not applied until a different user
 * with the `approver` role explicitly approves it. The proposer cannot
 * approve their own change (separation of duties).
 */

export const ROLES = {
  ADMIN: 'admin',
  APPROVER: 'approver',
  MANAGER: 'manager',
  AUDITOR: 'auditor',
  EMPLOYEE: 'employee',
};

// Hierarchy used for permission inheritance checks. Higher index = more access.
// Approver and admin are siblings, neither inherits from the other; they have
// distinct, intentionally-separated permissions.
export const ROLE_TIER = {
  [ROLES.EMPLOYEE]: 1,
  [ROLES.AUDITOR]: 2,
  [ROLES.MANAGER]: 3,
  [ROLES.ADMIN]: 4,
  [ROLES.APPROVER]: 4,
};

// ─── Permission matrix ───────────────────────────────────────────────────
// Each permission key describes a discrete action. The value lists the roles
// that hold the permission. Admin and approver have overlapping but distinct
// rights, admin can propose, approver can ratify. This is by design.
export const PERMISSIONS = {
  // Joint Assessments
  'assessment.create':         [ROLES.ADMIN],
  'assessment.view':           [ROLES.ADMIN, ROLES.APPROVER, ROLES.MANAGER, ROLES.AUDITOR],
  'assessment.editFields':     [ROLES.ADMIN],          // notes, description, non-sensitive metadata
  'assessment.assignTask':     [ROLES.ADMIN, ROLES.MANAGER],
  'assessment.changeStatus':   [ROLES.ADMIN],          // proposes only, change goes through approval
  'assessment.close':          [ROLES.ADMIN],          // proposes; approver must ratify
  'assessment.uploadDoc':      [ROLES.ADMIN, ROLES.MANAGER],
  'assessment.downloadDoc':    [ROLES.ADMIN, ROLES.APPROVER, ROLES.MANAGER, ROLES.AUDITOR],
  'assessment.viewDoc':        [ROLES.ADMIN, ROLES.APPROVER, ROLES.MANAGER, ROLES.AUDITOR],

  // Approvals
  'approval.approve':          [ROLES.APPROVER],
  'approval.reject':           [ROLES.APPROVER],
  'approval.viewQueue':        [ROLES.APPROVER, ROLES.ADMIN], // admin sees their own pending items

  // Reports
  'report.generate':           [ROLES.ADMIN],
  'report.view':               [ROLES.ADMIN, ROLES.APPROVER, ROLES.AUDITOR],
  'report.download':           [ROLES.ADMIN, ROLES.APPROVER, ROLES.AUDITOR],

  // Pay bands
  'payBand.edit':              [ROLES.ADMIN],
  'payBand.publish':           [ROLES.APPROVER],       // publishing a new band requires approver

  // User management, never delegated to admin alone in the SaaS phase
  'user.invite':               [ROLES.APPROVER],
  'user.changeRole':           [ROLES.APPROVER],
};

/**
 * `requiresApproval` set lists the actions that, when proposed by an admin,
 * are routed through the approval queue rather than applied immediately.
 * The audit trail records both the proposal and the eventual ratification.
 */
export const APPROVAL_GATED_ACTIONS = new Set([
  'assessment.changeStatus',
  'assessment.close',
  'payBand.publish',
  'user.invite',
  'user.changeRole',
]);

/**
 * Auto-assignment routing matrix
 * ───────────────────────────────
 * Maps task TYPE to the role that should perform it. When a task is created
 * without an explicit assignee, the system picks the user from the registered
 * users list whose role matches and whose department matches the parent
 * assessment (when applicable).
 */
export const TASK_ROUTING = {
  'data-review':         { role: ROLES.MANAGER,  scope: 'department' },
  'market-analysis':     { role: ROLES.ADMIN,    scope: 'global' },
  'remediation-plan':    { role: ROLES.ADMIN,    scope: 'department' },
  'works-council-liaison': { role: ROLES.APPROVER, scope: 'global' },
  'document-review':     { role: ROLES.APPROVER, scope: 'global' },
  'employee-comms':      { role: ROLES.MANAGER,  scope: 'department' },
  'legal-review':        { role: ROLES.APPROVER, scope: 'global' },
  'general':             { role: ROLES.ADMIN,    scope: 'global' },
};

/**
 * Returns true if the given role has the given permission.
 */
export const hasPermission = (role, permission) => {
  const allowed = PERMISSIONS[permission];
  if (!allowed) return false;
  return allowed.includes(role);
};

/**
 * Returns true if the given action requires routing through the approval
 * queue (rather than being applied immediately by an admin).
 */
export const isApprovalGated = (action) => APPROVAL_GATED_ACTIONS.has(action);

/**
 * Suggest a default assignee given a task type, department, and the
 * registered user directory. Returns the first matching user, or null if
 * no one matches (in which case admin must pick manually).
 */
export const suggestAssignee = (taskType, department, users) => {
  const route = TASK_ROUTING[taskType] || TASK_ROUTING['general'];
  if (!route || !Array.isArray(users)) return null;

  // Filter users by role
  const candidates = users.filter((u) => u.role === route.role && u.active !== false);
  if (candidates.length === 0) return null;

  // If the task is department-scoped, prefer a user from that department
  if (route.scope === 'department' && department) {
    const deptMatch = candidates.find((u) => u.department === department);
    if (deptMatch) return deptMatch;
  }

  // Fall back to the first available candidate (deterministic ordering)
  return candidates[0];
};
